/**
 * In-year (amortized) benefit — Brett, 2026-09-16.
 *
 * The board's Benefit column is ANNUALIZED EBITDA: what a project is
 * worth per full year once live. The board and C-level want the other
 * number too — what the current year actually captures given the
 * go-live date. A $400K/yr project that goes live Nov 1 has only
 * Nov + Dec left in 2026.
 *
 * Convention (aligned with Brett): prorate BY DAYS from go-live through
 * Dec 31 inclusive, over the days in that year; full rate from go-live
 * day one (no ramp). A project with no go-live date contributes
 * NOTHING — the caller renders that as "unscheduled" so the $0 is
 * visible rather than silently folded into a total.
 *
 * Go-live = the project's end_date, the same field the board's Go-live
 * column shows.
 *
 * ONE-TIME benefits (Brett, 2026-09-16 — ETE-318 NAPA EDI is a single
 * stocking order): the figure is not $/year, so it is never prorated.
 * It counts IN FULL in the year it lands (go-live year), $0 in any
 * other year, and is left out of the "annual" rollup entirely.
 *
 * IN-YEAR OVERRIDE (Brett, 2026-09-22 — NAPA EDI is $304,200/yr but only
 * one quarterly hit, $76,050, lands in 2026): a project may carry an
 * entered figure for ONE year. When that year is the one being computed
 * it replaces the proration (and the one-time rule) for that row; the
 * annual column is untouched. Any other year ignores it.
 */

/** Days in a calendar year (366 on leap years). */
export function daysInYear(year: number): number {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
}

/** Parse a YYYY-MM-DD (or ISO datetime) as a LOCAL calendar date so a
 *  go-live of "2026-11-01" is Nov 1 everywhere, not Oct 31 in the US. */
function toLocalDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Days of `year` remaining from `goLive` through Dec 31, inclusive of
 * both ends. 0 when go-live is after that year; the full year when
 * go-live is before it (already live all year).
 */
export function daysRemainingInYear(goLive: Date, year: number): number {
  const dec31 = new Date(year, 11, 31);
  const jan1 = new Date(year, 0, 1);
  const start = goLive < jan1 ? jan1 : goLive;
  if (start > dec31) return 0;
  const msPerDay = 86_400_000;
  // +1: go-live day itself counts.
  return Math.round((dec31.getTime() - start.getTime()) / msPerDay) + 1;
}

/**
 * The share of `annual` captured in `year` given `goLiveIso`.
 * null when there's no usable go-live date (unscheduled) or no benefit.
 */
export function inYearBenefit(
  annual: number | null | undefined,
  goLiveIso: string | null | undefined,
  year: number,
  oneTime = false,
): number | null {
  if (annual == null || !goLiveIso) return null;
  const goLive = toLocalDate(goLiveIso);
  if (!goLive) return null;
  // One-time: the whole amount lands in the go-live year, nothing else.
  if (oneTime) return goLive.getFullYear() === year ? Math.round(annual) : 0;
  const days = daysRemainingInYear(goLive, year);
  return Math.round((annual * days) / daysInYear(year));
}

export type BenefitRow = {
  annual_benefit?: number | null;
  end_date?: string | null;
  benefit_one_time?: boolean | null;
  in_year_benefit_override?: number | null;
  in_year_benefit_override_year?: number | null;
};

/** The entered in-year figure for `year`, or null when none applies. */
export function inYearOverride(row: BenefitRow, year: number): number | null {
  return row.in_year_benefit_override != null && row.in_year_benefit_override_year === year
    ? Math.round(row.in_year_benefit_override)
    : null;
}

/** In-year benefit for a project row: the entered override for this
 *  year if any, else the proration / one-time rule. */
export function inYearBenefitForRow(row: BenefitRow, year: number): number | null {
  const o = inYearOverride(row, year);
  if (o != null) return o;
  return inYearBenefit(row.annual_benefit, row.end_date, year, !!row.benefit_one_time);
}

/** Sum of in-year benefit across projects; unscheduled rows add 0. */
export function inYearBenefitTotal(rows: BenefitRow[], year: number): number {
  return rows.reduce((s, p) => s + (inYearBenefitForRow(p, year) ?? 0), 0);
}

/** Sum of RECURRING ($/yr) benefit only — one-time amounts are not
 *  annual, so they never inflate the "Annual benefit" total. */
export function annualBenefitTotal(rows: BenefitRow[]): number {
  return rows.reduce(
    (s, p) => s + (p.benefit_one_time ? 0 : (p.annual_benefit ?? 0)),
    0,
  );
}

/**
 * COST AVOIDANCE (Brett, 2026-10-16). Many projects carry no EBITDA but
 * save someone's time: a finance person at $65/hr who gets 10 hours a
 * month back is $650/month, $7,800/yr. Entered as hours saved per month
 * and priced at the blended hourly rate (Settings → Cost) × 12; an
 * entered annual dollar figure, when present, replaces the arithmetic.
 */
export type CostAvoidanceRow = {
  cost_avoidance_hours_per_month?: number | null;
  cost_avoidance_annual?: number | null;
};

/** Annual cost avoidance in dollars, or null when nothing is entered. */
export function costAvoidance(row: CostAvoidanceRow, hourlyRate: number): number | null {
  if (row.cost_avoidance_annual != null) return Math.round(row.cost_avoidance_annual);
  if (row.cost_avoidance_hours_per_month != null && row.cost_avoidance_hours_per_month > 0) {
    return Math.round(row.cost_avoidance_hours_per_month * 12 * hourlyRate);
  }
  return null;
}

/** Sum of cost avoidance across rows; rows with nothing entered add 0. */
export function costAvoidanceTotal(rows: CostAvoidanceRow[], hourlyRate: number): number {
  return rows.reduce((s, r) => s + (costAvoidance(r, hourlyRate) ?? 0), 0);
}
